
import { Component, Prop, Vue } from 'vue-property-decorator';

import { sharedState } from '../../../../../../framework/state';

import {
    deleteResource,
    getResource,
    getResourcePagingNew,
    getResourcesNew,
    putResource,
} from '../../../../../../framework/client/resource';
import { SalesInvoiceRow } from '../../../../../../../common/application/model/SalesInvoiceRow';
import { dateToDbDateString } from '../../../../../../../common/framework/util/convert';
import { PurchaseInvoiceRow } from '../../../../../../../common/application/model/PurchaseInvoiceRow';
import { ApplicationResource } from '../../../../../../../common/application/enumeration/ApplicationResource';

import { FrameworkUserRole } from '../../../../../../../common/framework/enumeration/FrameworkUserRole';
import { ApplicationUserRole } from '../../../../../../../common/application/enumeration/ApplicationUserRole';
import { errorToast, successToast } from '../../../../../service/toast_service';
import { formatCurrency } from '../../../../../service/helper_service';
import ParameterService from '../../../../../service/parameter_service';

@Component
export default class SalesInvoiceRows extends Vue {
    readonly resourceType = ApplicationResource.SALES_INVOICE_ROW;
    parameterService = new ParameterService(this.resourceType);

    @Prop(String) readonly salesInvoiceId!: string;

    shared = sharedState;
    local = {
        rows: [] as SalesInvoiceRow[],
        total: 0,
        loading: false,
        selected: undefined as SalesInvoiceRow | undefined,
        parameters: this.parameterService.get(),
    };

    async mounted() {
        await this.loadAsyncData();
    }

    async onSortChange(field: string, order: 'desc' | 'asc') {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    add() {
        this.$router.push(`/${ApplicationResource.SALES_INVOICE}/${this.salesInvoiceId}/add-row`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        if (this.local.selected.purchaseInvoiceRowId) {
                            const purchaseInvoiceRow = (await getResource<PurchaseInvoiceRow>(
                                ApplicationResource.PURCHASE_INVOICE_ROW,
                                this.local.selected.purchaseInvoiceRowId,
                            ))!!;
                            await putResource<PurchaseInvoiceRow>(
                                ApplicationResource.PURCHASE_INVOICE_ROW,
                                purchaseInvoiceRow.id,
                                {
                                    ...purchaseInvoiceRow,
                                    salesInvoiceRowId: undefined,
                                },
                            );
                        }

                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    dateToString(date: string | undefined): string {
        if (!date) {
            return '';
        }

        return dateToDbDateString(new Date(date));
    }

    view() {
        if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}`);
        }
    }

    onRowClick(row: SalesInvoiceRow) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    private async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();
        if (this.salesInvoiceId) {
            backendParameters.set('salesInvoiceId', this.salesInvoiceId);
        }

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;
        this.local.rows = [];
        const rows = await getResourcesNew<SalesInvoiceRow>(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        );

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
